import { ButtonProps } from '@mui/material';
import moment from 'moment';
import { useCallback, useMemo } from 'react';

import { useLoggedInEmployeeId } from '~/components/Auth';
import { AbsenceStatus } from '~/data/absences';
import { AbsenceEntry } from '~/data/absences/types';
import { useEmployeeAbsenceData } from '~/data/absences/useEmployeeAbsenceData';
import { TabModules, useCompanyData, useModulesConfig } from '~/data/company';
import { EnumListKey, useAllEnums } from '~/data/enums';
import { PeopleTabs } from '~/data/tabs';
import { useInstantAbsences } from '~/features/AbsenceChanges/InstantAbsences';
import { UseDialogStateOutput, useDialogState } from '~/hooks/useDialogState';
import { filterActiveEnums } from '~/utils/helper';

export interface UseAddAbsentTodayOutput {
  employeeId: number;
  loading: boolean;
  failedToLoad: boolean;
  todaysAbsence?: AbsenceEntry;
  add1DayButtonProps: ButtonProps;
  add2DayButtonProps: ButtonProps;
  isTabDisabled: boolean;
  defaultSickLeaveType: string;
  sickLeavesNotConfigured: boolean;
  addOtherAbsenceDialogState: UseDialogStateOutput;
}

export const useAddAbsentToday = (): UseAddAbsentTodayOutput => {
  const {
    companyData,
    isLoading: companyDataLoading,
    failedToLoad: companyDataFailedToLoad,
  } = useCompanyData();
  const { getEnums } = useAllEnums();

  const employeeId = useLoggedInEmployeeId()!;
  const { isModuleTabEnabled } = useModulesConfig();

  const {
    isLoading: isLoadingAbsenceData,
    failedToLoad: failedToLoadAbsenceData,
    absenceEntries,
  } = useEmployeeAbsenceData({
    employeeId,
  });

  const { getInstantAbsence, getDateRangeByDays, isSubmitting, reasonOptions } =
    useInstantAbsences({
      employeeId,
    });

  const addOtherAbsenceDialogState = useDialogState();

  const todaysAbsence = useMemo(() => {
    return absenceEntries.find((entry) => {
      const { status, startDate, endDate } = entry;

      const hasOverlapWithToday =
        (!!startDate &&
          !!endDate &&
          moment().isBetween(startDate, endDate, 'day', '[]')) ||
        (!!startDate && !endDate && moment().isSameOrAfter(startDate));

      return status !== AbsenceStatus.REJECTED && hasOverlapWithToday;
    });
  }, [absenceEntries]);

  const { isTabDisabled, sickLeavesNotConfigured, defaultSickLeaveType } =
    useMemo(() => {
      const activeShortAbsences = filterActiveEnums(
        getEnums(EnumListKey.ABSENCE_SHORT_REASON),
      );

      const activeShortAbsencesCodes = activeShortAbsences.map((e) => e.code);

      const [defaultSickLeaveType] = (
        companyData?.config.fComLeaveTypes.fComSickLeaveTypes || []
      ).filter((lt) => activeShortAbsencesCodes.includes(lt));

      const isTabDisabled = !isModuleTabEnabled(
        TabModules.PEOPLE,
        PeopleTabs.Absences,
      );

      const sickLeavesNotConfigured = !defaultSickLeaveType || isTabDisabled;

      return {
        isTabDisabled,
        sickLeavesNotConfigured,
        defaultSickLeaveType,
      };
    }, [isModuleTabEnabled, companyData, getEnums]);

  const { add1DayButtonProps, add2DayButtonProps } = useMemo(() => {
    const absenceReason = reasonOptions.find(
      (reason) => reason.code === defaultSickLeaveType,
    );

    const getButtonProps = (days: number) => {
      if (absenceReason) {
        const { buttonProps } = getInstantAbsence({
          absenceReason,
          ...getDateRangeByDays(new Date(), days),
        });

        return buttonProps;
      }

      return {};
    };

    return {
      add1DayButtonProps: getButtonProps(1),
      add2DayButtonProps: getButtonProps(2),
    };
  }, [
    defaultSickLeaveType,
    getDateRangeByDays,
    getInstantAbsence,
    reasonOptions,
  ]);

  const extendButtonPropsDisabledCondition = useCallback(
    (buttonProps: ButtonProps, disabled: boolean) => {
      return {
        ...buttonProps,
        disabled: buttonProps.disabled || disabled,
      };
    },
    [],
  );

  return {
    employeeId,
    loading: companyDataLoading || isLoadingAbsenceData,
    failedToLoad: companyDataFailedToLoad || failedToLoadAbsenceData,
    add1DayButtonProps: extendButtonPropsDisabledCondition(
      add1DayButtonProps,
      sickLeavesNotConfigured || isSubmitting,
    ),
    add2DayButtonProps: extendButtonPropsDisabledCondition(
      add2DayButtonProps,
      sickLeavesNotConfigured || isSubmitting,
    ),
    todaysAbsence,
    isTabDisabled,
    defaultSickLeaveType,
    sickLeavesNotConfigured,
    addOtherAbsenceDialogState,
  };
};
